/**
 * Tells us what the current user is allowed to do based on what roles they have.
 * The user roles as returned by the API need a bit of parsing to be useful in the admin UI.
 *
 * Two roles are not used for access permission: MDD and TeamMedical because these are mobile app only
 * see https://docs.google.com/spreadsheets/d/14l6xBXMaXhNMZ2ObLbo_2-DEmYAw4xnCQz2Fif8jbOg/edit#gid=0
 *
 * The api will only return entities we are allowed to see, which makes our permissions calculations much easier.
 * We rely on the api to 403 if we really do not have access: the permissions checks here are close enough but
 * not 100% foolproof because we are on the front end and cannot prevent users poking around in the javascript.
 *
 * Works in two main ways
 * - supports components that show text and a link to the object if access is allowed, just the text if not.
 *   These usually need a full API object to determine if access is allowed, because the object has attached team, org
 *   or user info that is needed to make the decision. In most cases, we are processing a list of these rich objects so
 *   the information is right there and costs nothing extra to fetch
 * - a coarse filter on major navigation: all we have is the object id (no detail about it). So we accept that we might
 *   get it wrong and err on the side of allowing access, because the API will stop us if we are not allowed to see it.
 *   This is mainly used for routing and the left menu
 */
import {
  Assessment,
  ComprehensiveAssessment,
  Event,
  Fixture,
  ICCReview,
  Org,
  Player,
  Role,
  Team,
  Tournament,
  User,
  Venue,
} from 'src/api';
import { MainMenuOption } from 'src/service/MainMenu';
import Constants from 'src/constants';
import { AbstractUserRole } from 'src/service/UserPermission/AbstractUserRole';
import { ResearcherRole } from 'src/service/UserPermission/ResearcherRole';
import { AdminRole } from 'src/service/UserPermission/AdminRole';
import { ReportViewerRole } from 'src/service/UserPermission/ReportViewerRole';
import { ICCRole } from 'src/service/UserPermission/ICCRole';
import { UnionAdminRole } from 'src/service/UserPermission/UnionAdminRole';
import { ComprehensiveReviewerRole } from 'src/service/UserPermission/ComprehensiveReviewerRole';
import { ComprehensiveAssessorRole } from 'src/service/UserPermission/ComprehensiveAssessorRole';
import { TournamentAdminRole } from 'src/service/UserPermission/TournamentAdminRole';
import { MedicalReviewerRole } from 'src/service/UserPermission/MedicalReviewerRole';
import { TeamAdminRole } from 'src/service/UserPermission/TeamAdminRole';
import { differenceInDays, differenceInHours } from 'date-fns';
import { UnionResearcherRole } from 'src/service/UserPermission/UnionResearcherRole';

// a composite of all the roles the user has
export class UserPermissions extends AbstractUserRole {
  private emptyMap: Map<number, string> = new Map();
  private roles: Map<Role, AbstractUserRole> = new Map<
    Role,
    AbstractUserRole
  >();
  private userId: number;

  isSuperDude(): boolean {
    return this.roles.has(Role.Admin);
  }

  getComprehensiveTeams() {
    return (
      this.roles.get(Role.Comprehensive_Assessor)?.getList() ?? this.emptyMap
    );
  }

  getComprehensiveReviewerTournaments() {
    return (
      this.roles.get(Role.Comprehensive_Reviewer)?.getList() ?? this.emptyMap
    );
  }

  getTeams() {
    return this.roles.get(Role.Team_Admin)?.getList() ?? this.emptyMap;
  }

  getUnions() {
    return this.roles.get(Role.Union_Admin)?.getList() ?? this.emptyMap;
  }

  canSeeAnything(): boolean {
    return this.roles.size > 0;
  }

  /**
   * Can the user navigate to this part of the app and see the data there?
   * Used to calculate left menu visibility
   */
  canView(option: MainMenuOption): boolean {
    return [...this.roles.values()].some((role) => role.canView(option));
  }

  canViewUnion(union: Org): boolean {
    return [...this.roles.values()].some((role) => role.canViewUnion(union));
  }

  canEditUnion(union: Org): boolean {
    return [...this.roles.values()].some((role) => role.canEditUnion(union));
  }

  canViewTeam(team: Team): boolean {
    return [...this.roles.values()].some((role) => role.canViewTeam(team));
  }

  canEditTeam(team: Team): boolean {
    return [...this.roles.values()].some((role) => role.canEditTeam(team));
  }

  canViewPlayer(player: Player): boolean {
    return [...this.roles.values()].some((role) => role.canViewPlayer(player));
  }

  canEditPlayer(player: Player): boolean {
    return [...this.roles.values()].some((role) => role.canEditPlayer(player));
  }

  canCommentEvent(event: Event): boolean {
    return [...this.roles.values()].some((role) => role.canCommentEvent(event));
  }

  canEditEvent(event: Event): boolean {
    return [...this.roles.values()].some((role) => role.canEditEvent(event));
  }

  canReviewEvent(event: Event): boolean {
    return [...this.roles.values()].some((role) => role.canReviewEvent(event));
  }

  canDownloadEventPdf(event: Event): boolean {
    return [...this.roles.values()].some((role) =>
      role.canDownloadEventPdf(event),
    );
  }

  canCreateComprehensiveAssessment(player: Player): boolean {
    return [...this.roles.values()].some((role) =>
      role.canCreateComprehensiveAssessment(player),
    );
  }

  canViewComprehensiveAssessment(assessment: ComprehensiveAssessment): boolean {
    return [...this.roles.values()].some((role) =>
      role.canViewComprehensiveAssessment(assessment),
    );
  }

  private comprehensiveIsLocked(assessment: ComprehensiveAssessment): boolean {
    // editable if it was completed less than X hours ago, or if it is not completed
    if (!assessment.completedAt) return false;
    return (
      differenceInHours(Date.now(), Date.parse(assessment.completedAt)) >=
      Constants.comprehensiveHoursUntilLocked
    );
  }

  canReviewComprehensiveAssessment(
    assessment: ComprehensiveAssessment,
  ): boolean {
    if (!assessment.completedAt) return false;
    return [...this.roles.values()].some((role) =>
      role.canReviewComprehensiveAssessment(assessment),
    );
  }

  canEditComprehensiveAssessment(assessment: ComprehensiveAssessment): boolean {
    return (
      !this.comprehensiveIsLocked(assessment) &&
      [...this.roles.values()].some((role) =>
        role.canEditComprehensiveAssessment(assessment),
      )
    );
  }

  canViewAssessment(assessment: Assessment): boolean {
    return [...this.roles.values()].some((role) =>
      role.canViewAssessment(assessment),
    );
  }

  canViewUser(user: User): boolean {
    return [...this.roles.values()].some((role) => role.canViewUser(user));
  }

  canEditUser(user: User): boolean {
    // user cannot edit themselves unless they are Admin so they can't add roles to their own account: use My Account for editing your own name etc
    if (user.id === this.userId && !this.isSuperDude) {
      return false;
    }
    return [...this.roles.values()].some((role) => role.canEditUser(user));
  }

  canViewTournament(tournament: Tournament): boolean {
    return [...this.roles.values()].some((role) =>
      role.canViewTournament(tournament),
    );
  }

  canEditTournament(tournament: Tournament): boolean {
    return [...this.roles.values()].some((role) =>
      role.canEditTournament(tournament),
    );
  }

  canViewVenue(venue: Venue): boolean {
    return [...this.roles.values()].some((role) => role.canViewVenue(venue));
  }

  canEditVenue(venue: Venue): boolean {
    return [...this.roles.values()].some((role) => role.canEditVenue(venue));
  }

  canAddFixture(tournament: Tournament | undefined = undefined): boolean {
    return [...this.roles.values()].some((role) =>
      role.canAddFixture(tournament),
    );
  }

  canViewFixture(fixture: Fixture): boolean {
    return [...this.roles.values()].some((role) =>
      role.canViewFixture(fixture),
    );
  }

  canEditFixture(fixture: Fixture): boolean {
    return [...this.roles.values()].some((role) =>
      role.canEditFixture(fixture),
    );
  }

  canAddMDDToFixture(fixture: Fixture): boolean {
    return [...this.roles.values()].some((role) =>
      role.canAddMDDToFixture(fixture),
    );
  }

  private iccReviewIsLocked(review: ICCReview): boolean {
    if (!review.completedAt) return false;
    return (
      differenceInDays(Date.now(), Date.parse(review.completedAt)) >
      Constants.iccReviewDaysUntilLocked
    );
  }

  private iccReviewIsMine(review: ICCReview): boolean {
    return (
      review.assignedTo?.id === this.userId ||
      review.assignedTo?.id === undefined ||
      this.isSuperDude()
    );
  }

  canViewICCReview(review: ICCReview): boolean {
    return (
      this.iccReviewIsMine(review) &&
      [...this.roles.values()].some((role) => role.canViewICCReview(review))
    );
  }

  canEditICCReview(review: ICCReview): boolean {
    return (
      this.iccReviewIsMine(review) &&
      !this.iccReviewIsLocked(review) &&
      [...this.roles.values()].some((role) => role.canEditICCReview(review))
    );
  }

  canToggleICCCompliance(): boolean {
    return [...this.roles.values()].some((role) =>
      role.canToggleICCCompliance(),
    );
  }

  canIgnoreEvents(): boolean {
    return [...this.roles.values()].some((role) => role.canIgnoreEvents());
  }

  canSeeCompletedTournaments(): boolean {
    return [...this.roles.values()].some((role) =>
      role.canSeeCompletedTournaments(),
    );
  }

  constructor(user: User) {
    super();

    this.userId = user?.id ?? 0;
    user.roles?.forEach((role) => {
      switch (role.role) {
        case Role.Admin:
          if (!this.roles.has(Role.Admin))
            this.roles.set(Role.Admin, new AdminRole());
          break;
        case Role.Researcher:
          if (!this.roles.has(Role.Researcher))
            this.roles.set(Role.Researcher, new ResearcherRole());
          break;
        case Role.Report_Viewer:
          if (!this.roles.has(Role.Report_Viewer))
            this.roles.set(Role.Report_Viewer, new ReportViewerRole());
          break;
        case Role.ICC:
          if (!this.roles.has(Role.ICC))
            this.roles.set(Role.ICC, new ICCRole());
          break;
        case Role.Union_Admin:
          if (!this.roles.has(Role.Union_Admin))
            this.roles.set(Role.Union_Admin, new UnionAdminRole());
          this.roles
            .get(Role.Union_Admin)
            ?.addToList(role.org?.id, role.org?.name);
          break;
        case Role.Union_Researcher:
          if (!this.roles.has(Role.Union_Researcher))
            this.roles.set(Role.Union_Researcher, new UnionResearcherRole());
          this.roles
            .get(Role.Union_Researcher)
            ?.addToList(role.org?.id, role.org?.name);
          break;
        case Role.Team_Admin:
          if (!this.roles.has(Role.Team_Admin))
            this.roles.set(Role.Team_Admin, new TeamAdminRole());
          this.roles
            .get(Role.Team_Admin)
            ?.addToList(role.team?.id, role.team?.name);
          break;
        case Role.Medical_Reviewer:
          if (!this.roles.has(Role.Medical_Reviewer))
            this.roles.set(Role.Medical_Reviewer, new MedicalReviewerRole());
          this.roles
            .get(Role.Medical_Reviewer)
            ?.addToList(role.team?.id, role.team?.name);
          break;
        case Role.Tournament_Admin:
          if (!this.roles.has(Role.Tournament_Admin))
            this.roles.set(Role.Tournament_Admin, new TournamentAdminRole());
          this.roles
            .get(Role.Tournament_Admin)
            ?.addToList(role.tournament?.id, role.tournament?.name);
          break;
        case Role.Comprehensive_Assessor:
          if (!this.roles.has(Role.Comprehensive_Assessor))
            this.roles.set(
              Role.Comprehensive_Assessor,
              new ComprehensiveAssessorRole(),
            );
          this.roles
            .get(Role.Comprehensive_Assessor)
            ?.addToList(role.team?.id, role.team?.name);
          break;
        case Role.Comprehensive_Reviewer:
          if (!this.roles.has(Role.Comprehensive_Reviewer))
            this.roles.set(
              Role.Comprehensive_Reviewer,
              new ComprehensiveReviewerRole(),
            );
          this.roles
            .get(Role.Comprehensive_Reviewer)
            ?.addToList(role.tournament?.id, role.tournament?.name);
          break;
        // case RoleNameEnum.Team_User: // cannot log in to admin ui
        //   break;
        // case RoleNameEnum.Match_Day_Doctor: // cannot log in to admin ui
        //   break;
        default:
          // ignore: it's an invalid record
          break;
      }
    });
  }
}
